import React, { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, CardHeader, Grid, TextField,
  Button, Stack, FormControl, InputLabel, Select, MenuItem
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import SearchIcon from '@mui/icons-material/Search'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import { useSnackbar } from 'notistack'
import normaService from '../services/normaService'
import crudsService from '../services/crudsService'
import CrudTable from '../components/common/CrudTable'
import PageHeader from '../components/common/PageHeader'
import ConfirmDialog from '../components/common/ConfirmDialog'

const COLUMNS = [
  { field: 'descripcion', header: 'Descripción' },
  { field: 'tipo_caracter_label', header: 'Tipo Carácter' },
]

export default function VozNormaLegal() {
  const { enqueueSnackbar } = useSnackbar()

  const [casoUso, setCasoUso] = useState('new')
  const [id, setId] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [tipoCaracter, setTipoCaracter] = useState('')
  const [buscar, setBuscar] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [rowToDelete, setRowToDelete] = useState(null)

  useEffect(() => { loadData() }, [])

  const loadData = (filtro = '') => {
    setLoading(true)
    normaService.getVoces(null, filtro || undefined).then(res => {
      const list = (res.data?.data || []).map(r => ({
        ...r,
        tipo_caracter_label: r.tipo_caracter === 'i' ? 'Individual' : r.tipo_caracter === 'g' ? 'General' : 'Ambos',
      }))
      setRows(list)
    }).catch(() => enqueueSnackbar('Error al cargar voces', { variant: 'error' }))
      .finally(() => setLoading(false))
  }

  const clearForm = () => {
    setCasoUso('new'); setId(''); setDescripcion(''); setTipoCaracter('')
  }

  const handleEdit = (row) => {
    setCasoUso('edit'); setId(row.id); setDescripcion(row.descripcion); setTipoCaracter(row.tipo_caracter || '')
  }

  const handleDeleteRequest = (row) => { setRowToDelete(row); setConfirmOpen(true) }

  const handleDeleteConfirm = async () => {
    setConfirmOpen(false)
    try {
      await crudsService.vozNormaLegal('delete', { id: rowToDelete.id })
      enqueueSnackbar('Voz de Norma Legal eliminada', { variant: 'success' })
      loadData(buscar)
    } catch { enqueueSnackbar('Error al eliminar', { variant: 'error' }) }
    setRowToDelete(null)
  }

  const handleAceptar = async () => {
    if (!descripcion.trim()) {
      enqueueSnackbar('Debe indicar la descripción de la Voz de Norma Legal', { variant: 'warning' }); return
    }
    const object = { descripcion, tipo_caracter: tipoCaracter }
    if (casoUso === 'edit') object.id = id
    try {
      const res = await crudsService.vozNormaLegal(casoUso, object)
      if (res.data?.code !== undefined) {
        enqueueSnackbar(`Voz de Norma Legal ${casoUso === 'new' ? 'agregada' : 'actualizada'}`, { variant: 'success' })
        clearForm(); loadData(buscar)
      } else {
        enqueueSnackbar('Error al guardar', { variant: 'error' })
      }
    } catch { enqueueSnackbar('Error al guardar', { variant: 'error' }) }
  }

  return (
    <Box>
      <PageHeader title="Voz de Norma Legal" subtitle="Gestión de voces de normas legales" icon={RecordVoiceOverIcon} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title={casoUso === 'new' ? 'Nueva Voz' : 'Editar Voz'} titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
            <CardContent>
              <Stack spacing={2}>
                <TextField fullWidth size="small" label="Descripción *" value={descripcion}
                  onChange={e => setDescripcion(e.target.value)} inputProps={{ maxLength: 200 }} />
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo Carácter</InputLabel>
                  <Select value={tipoCaracter} label="Tipo Carácter" onChange={e => setTipoCaracter(e.target.value)}>
                    <MenuItem value="">Ambos</MenuItem>
                    <MenuItem value="i">Individual</MenuItem>
                    <MenuItem value="g">General</MenuItem>
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button variant="outlined" startIcon={<CancelIcon />} onClick={clearForm} size="small">Cancelar</Button>
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleAceptar} size="small">
                    {casoUso === 'new' ? 'Agregar' : 'Actualizar'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Listado"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" placeholder="Buscar..." value={buscar} onChange={e => setBuscar(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadData(buscar)} sx={{ width: 160 }} />
                  <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={() => loadData(buscar)}>Buscar</Button>
                </Stack>
              }
            />
            <CardContent sx={{ p: 0 }}>
              <CrudTable columns={COLUMNS} rows={rows} loading={loading} onEdit={handleEdit} onDelete={handleDeleteRequest} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Voz de Norma Legal"
        message={`¿Confirma la eliminación de "${rowToDelete?.descripcion}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setRowToDelete(null) }}
      />
    </Box>
  )
}
